import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Spinner, Alert, Button, Breadcrumb, BreadcrumbItem, Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle
} from 'chart.js';
import { useGetOptionsMutation } from '../../slices/commonApiSlice';
import { parseUserInfo } from '../../utils/parseUtil';
import { parseOptionsData } from '../../utils/parseOptionsData';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, ChartTitle);

const QuickInfoDashboard = () => {
  // State management
  const [quickInfoData, setQuickInfoData] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const getPreviousMonthDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  };
  const [selectedDate, setSelectedDate] = useState(getPreviousMonthDate());
  const [error, setError] = useState(null);
  
  // Employee type filter state
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [selectedEmployeeType, setSelectedEmployeeType] = useState('');
  const [getOptions] = useGetOptionsMutation();

  const { userInfo } = useSelector((state) => state.auth);

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_URL;
  const API_ENDPOINT = "/api/WebServiceQI.aspx";

  // Get company code from userInfo
  const getCompanyCode = () => {
    return userInfo[13]?.InfoField || '1';
  };

  // Main API call function
  const fetchQuickInfoData = async (month, year, empType = '') => {
    setLoading(true);
    setError(null);
    
    try {
      // Format parameters exactly as required
      const reqMonth = month.toString().padStart(2, '0'); // "01", "02", etc.
      const reqYear = year.toString(); // "2025"
      const companyCode = getCompanyCode();

      console.log(`title: MakeSalarySummaryForQI\nReqCompanyCode: ${companyCode}\nReqYear: ${reqYear}\nReqMonth: ${reqMonth}\nReqPayEmpType: ${empType}`);

      // Create FormData for multipart request
      const formData = new FormData();
      formData.append('title', 'MakeSalarySummaryForQI');
      formData.append('ReqCompanyCode', companyCode);
      formData.append('ReqYear', reqYear);
      formData.append('ReqMonth', reqMonth);
      if (empType) {
        formData.append('ReqPayEmpType', empType);
      }

      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINT}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 10000, // 10 second timeout
      });

      console.log("Response Body:", response.data);

      if (response.status === 200) {
        // Parse response
        const jsonResponse = response.data.split('||JasonEnd')[0].trim();
        const decodedJson = JSON.parse(jsonResponse);
        const decodedJsonData = JSON.parse(decodedJson[0]['JSONData1']);
        const decodedJsonData2 = decodedJson[0]['JSONData2'] ? JSON.parse(decodedJson[0]['JSONData2']) : [];

        setQuickInfoData(decodedJsonData);
        setDetailedData(decodedJsonData2);
        console.log("quickinfo list fetched:", decodedJsonData.length);
        console.log("detailed data fetched:", decodedJsonData2.length);
        console.log("Detailed Data Structure:", decodedJsonData2);
      } else {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }
    } catch (error) {
      console.error("Error:", error);
      setError(error.response?.data?.message || error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      if (!userInfo) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Format parameters exactly as required
        const month = selectedDate.getMonth() + 1;
        const year = selectedDate.getFullYear();
        const reqMonth = month.toString().padStart(2, '0'); // "01", "02", etc.
        const reqYear = year.toString(); // "2025"
        const companyCode = userInfo[13]?.InfoField || '1';

        console.log(`title: MakeSalarySummaryForQI\nReqCompanyCode: ${companyCode}\nReqYear: ${reqYear}\nReqMonth: ${reqMonth}\nReqPayEmpType: ${selectedEmployeeType}`);

        // Create FormData for multipart request
        const formData = new FormData();
        formData.append('title', 'MakeSalarySummaryForQI');
        formData.append('ReqCompanyCode', companyCode);
        formData.append('ReqYear', reqYear);
        formData.append('ReqMonth', reqMonth);
        if (selectedEmployeeType) {
          formData.append('ReqPayEmpType', selectedEmployeeType);
        }

        const response = await axios.post(`${API_BASE_URL}${API_ENDPOINT}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 10000, // 10 second timeout
        });

        console.log("Response Body:", response.data);

        if (response.status === 200) {
          // Parse response
          const jsonResponse = response.data.split('||JasonEnd')[0].trim();
          const decodedJson = JSON.parse(jsonResponse);
          const decodedJsonData = JSON.parse(decodedJson[0]['JSONData1']);
          const decodedJsonData2 = decodedJson[0]['JSONData2'] ? JSON.parse(decodedJson[0]['JSONData2']) : [];

          setQuickInfoData(decodedJsonData);
          setDetailedData(decodedJsonData2);
          console.log("quickinfo list fetched:", decodedJsonData.length);
          console.log("detailed data fetched:", decodedJsonData2.length);
          console.log("Detailed Data Structure:", decodedJsonData2);
        } else {
          throw new Error(`Failed to fetch data: ${response.status}`);
        }
      } catch (error) {
        console.error("Error:", error);
        setError(error.response?.data?.message || error.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedDate, selectedEmployeeType, userInfo, API_BASE_URL, API_ENDPOINT]);

  // Fetch employee types on component load
  useEffect(() => {
    const fetchEmployeeTypes = async () => {
      if (!userInfo) return;
      
      try {
        const reqCompanyCode = userInfo[13]?.InfoField || '1';
        
        const formData = new FormData();
        formData.append('title', 'GetEmployeeMasterDataList');
        formData.append('ReqCompanyCode', reqCompanyCode);

        const res = await getOptions(formData);
        const parsedOptions = parseUserInfo(res);
        const parsedData = parseOptionsData(parsedOptions);

        if (parsedData && parsedData.employeeTypes) {
          setEmployeeTypes(parsedData.employeeTypes);
        }
      } catch (err) {
        console.error('Error fetching employee types:', err);
      }
    };

    fetchEmployeeTypes();
  }, [getOptions, userInfo]);

  // Date change handler
  const handleDateChange = (newMonth, newYear) => {
    const newDate = new Date(newYear, newMonth - 1, 1);
    setSelectedDate(newDate);
  };

  // Employee type change handler
  const handleEmployeeTypeChange = (empType) => {
    setSelectedEmployeeType(empType);
  };

  // Refresh table data only
  const refreshTableData = async () => {
    if (!userInfo) return;
    
    setTableLoading(true);
    try {
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      const reqMonth = month.toString().padStart(2, '0');
      const reqYear = year.toString();
      const companyCode = userInfo[13]?.InfoField || '1';

      const formData = new FormData();
      formData.append('title', 'MakeSalarySummaryForQI');
      formData.append('ReqCompanyCode', companyCode);
      formData.append('ReqYear', reqYear);
      formData.append('ReqMonth', reqMonth);
      if (selectedEmployeeType) {
        formData.append('ReqPayEmpType', selectedEmployeeType);
      }

      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINT}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10000,
      });

      if (response.status === 200) {
        const jsonResponse = response.data.split('||JasonEnd')[0].trim();
        const decodedJson = JSON.parse(jsonResponse);
        const decodedJsonData2 = decodedJson[0]['JSONData2'] ? JSON.parse(decodedJson[0]['JSONData2']) : [];
        
        setDetailedData(decodedJsonData2);
        console.log("Table data refreshed:", decodedJsonData2.length);
        toast.success('Table data refreshed successfully!');
      }
    } catch (error) {
      console.error("Table refresh error:", error);
      toast.error('Failed to refresh table data');
    } finally {
      setTableLoading(false);
    }
  };

  // Helper function to find specific data items
  const findItem = (keyword) => {
    try {
      return quickInfoData.find(item => 
        (item.ItemName || "").toLowerCase().includes(keyword.toLowerCase())
      ) || { ItemName: "N/A", InfoField: "-" };
    } catch (error) {
      return { ItemName: "N/A", InfoField: "-" };
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === "-") return "₹0";
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  // Helper to get deduction values
  const getDeductionValue = (keyword) => {
    const item = quickInfoData.find(item => (item.ItemName || '').toLowerCase().includes(keyword.toLowerCase()));
    return item && item.InfoField && item.InfoField !== '-' ? parseFloat(item.InfoField.toString().replace(/[^\d.-]/g, '')) : 0;
  };

  // Deductions: EPF, ESI, TDS+RECTDS
  const epf = getDeductionValue('EPF');
  const esi = getDeductionValue('ESI');
  const tds = getDeductionValue('TDS');
  const rectds = getDeductionValue('RECTDS');
  const tdsTotal = tds + rectds;

  // Doughnut chart data
  const deductionChartData = {
    labels: ['EPF', 'ESI', 'TDS'],
    datasets: [
      {
        data: [epf, esi, tdsTotal],
        backgroundColor: ['#667eea', '#38ef7d', '#fbc2eb'],
        borderWidth: 1,
      },
    ],
  };

  // Net Pay Trend (last 6 months)
  const [netPayTrend, setNetPayTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    // Fetch net pay for last 6 months
    const fetchTrend = async () => {
      if (!userInfo) return;
      setTrendLoading(true);
      const now = new Date(selectedDate);
      const trendData = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const month = d.getMonth() + 1;
        const year = d.getFullYear();
        const reqMonth = month.toString().padStart(2, '0');
        const reqYear = year.toString();
        const companyCode = userInfo[13]?.InfoField || '1';
        const formData = new FormData();
        formData.append('title', 'MakeSalarySummaryForQI');
        formData.append('ReqCompanyCode', companyCode);
        formData.append('ReqYear', reqYear);
        formData.append('ReqMonth', reqMonth);
        if (selectedEmployeeType) {
          formData.append('ReqPayEmpType', selectedEmployeeType);
        }
        try {
          const response = await axios.post(`${API_BASE_URL}${API_ENDPOINT}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 10000,
          });
          if (response.status === 200) {
            const jsonResponse = response.data.split('||JasonEnd')[0].trim();
            const decodedJson = JSON.parse(jsonResponse);
            const decodedJsonData = JSON.parse(decodedJson[0]['JSONData1']);
            const netPayItem = decodedJsonData.find(item => (item.ItemName || '').toLowerCase().includes("employee's net salary"));
            trendData.push({
              label: d.toLocaleString('en', { month: 'short', year: '2-digit' }),
              value: netPayItem && netPayItem.InfoField && netPayItem.InfoField !== '-' ? parseFloat(netPayItem.InfoField.toString().replace(/[^\d.-]/g, '')) : 0,
            });
          }
        } catch (e) {
          trendData.push({ label: d.toLocaleString('en', { month: 'short', year: '2-digit' }), value: 0 });
        }
      }
      setNetPayTrend(trendData);
      setTrendLoading(false);
    };
    fetchTrend();
    // eslint-disable-next-line
  }, [selectedDate, selectedEmployeeType, userInfo, API_BASE_URL, API_ENDPOINT]);

  const netPayChartData = {
    labels: netPayTrend.map(d => d.label),
    datasets: [
      {
        label: 'Net Pay',
        data: netPayTrend.map(d => d.value),
        fill: false,
        borderColor: '#667eea',
        backgroundColor: '#667eea',
        tension: 0.3,
      },
    ],
  };

  // Helper functions for detailed data
  const getDetailedItems = (keyName) => {
    return detailedData.filter(item => item.ItemKeyName === keyName);
  };

  const formatNumber = (value) => {
    if (!value || value === "0" || value === "-" || value === "") return "0";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0" : numValue.toLocaleString('en-IN');
  };

  const getFieldValue = (item) => {
    // Check all possible InfoField values and return the first non-empty one
    return item.InfoField || item.InfoField1 || item.InfoField2 || item.InfoField3 || "0";
  };

  const getEmployeeData = (keyName) => {
    const items = getDetailedItems(keyName);
    const male = items.find(item => item.ItemName === "Male");
    const female = items.find(item => item.ItemName === "Female");
    
    // Get raw values
    const malePresent = male ? getFieldValue(male) : "0";
    const femalePresent = female ? getFieldValue(female) : "0";
    const maleAbsent = male ? (male.InfoField2 || "0") : "0";
    const femaleAbsent = female ? (female.InfoField2 || "0") : "0";
    
    // Calculate totals properly in frontend
    const maleTotalValue = parseInt(malePresent) + parseInt(maleAbsent);
    const femaleTotalValue = parseInt(femalePresent) + parseInt(femaleAbsent);
    const grandTotalValue = maleTotalValue + femaleTotalValue;
    
    return {
      male: malePresent,
      female: femalePresent, 
      total: (parseInt(malePresent) + parseInt(femalePresent)).toString(),
      maleAbsent: maleAbsent,
      femaleAbsent: femaleAbsent,
      totalAbsent: (parseInt(maleAbsent) + parseInt(femaleAbsent)).toString(),
      maleTotal: maleTotalValue.toString(),
      femaleTotal: femaleTotalValue.toString(),
      // Return the calculated grand total rather than any backend value
      grandTotal: grandTotalValue.toString()
    };
  };

  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/dashboard" className='text-success text-decoration-none'>
            Dashboard
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>
          Quick Info Dashboard
        </BreadcrumbItem>
      </Breadcrumb>

      <Container fluid className="px-3 px-md-4" style={{ maxWidth: "1600px" }}>
        <h4 className="text-center mb-4 fw-bold text-primary">Quick Info Dashboard</h4>

        {/* Date Selector */}
        <Row className="mb-4 align-items-center justify-content-between g-3">
          <Col lg={6} md={12} className="d-flex align-items-center justify-content-center justify-content-lg-start">
            <h5 className="mb-0">Welcome, <span className="fw-bold text-primary">{userInfo && userInfo[2]?.InfoField ? userInfo[2].InfoField : 'User'}</span></h5>
          </Col>
          <Col lg={6} md={12} className="d-flex justify-content-center justify-content-lg-end">
            <Card className="shadow-sm border-0" style={{ minWidth: 420, maxWidth: 550 }}>
              <Card.Body className="p-3">
                <Row className="align-items-center g-2">
                  <Col xs={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-muted small">Month</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedDate.getMonth() + 1}
                        onChange={(e) => handleDateChange(parseInt(e.target.value), selectedDate.getFullYear())}
                        className="form-select-sm"
                      >
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(0, i).toLocaleString('en', { month: 'long' })}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col xs={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-muted small">Year</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedDate.getFullYear()}
                        onChange={(e) => handleDateChange(selectedDate.getMonth() + 1, parseInt(e.target.value))}
                        className="form-select-sm"
                      >
                        {Array.from({length: 10}, (_, i) => {
                          const year = new Date().getFullYear() - 5 + i;
                          return <option key={year} value={year}>{year}</option>
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col xs={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-muted small">Employee Type</Form.Label>
                      <Form.Control
                        as="select"
                        value={selectedEmployeeType}
                        onChange={(e) => handleEmployeeTypeChange(e.target.value)}
                        className="form-select-sm"
                      >
                        <option value="">All Types</option>
                        {employeeTypes.map((type, index) => (
                          <option key={index} value={type}>
                            {type}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* After the welcome/date row, add the summary card: */}
        <Row className="mb-4">
          <Col>
            <Card className="shadow-sm" style={{ borderRadius: 12 }}>
              <Card.Body className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: '2.5rem', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div className="text-uppercase small text-muted fw-bold" style={{ letterSpacing: 1 }}>Employees' Net Pay</div>
                    <div className="h4 fw-bold mb-0">{formatCurrency(findItem("Employee's Net Salary").InfoField)}</div>
                  </div>
                  <div className="vr mx-3 d-none d-md-block" style={{ height: 40 }}></div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div className="text-uppercase small text-muted fw-bold" style={{ letterSpacing: 1 }}>Payment Date</div>
                    <div className="fw-bold mb-0">{findItem("Payment Date").InfoField}</div>
                  </div>
                  <div className="vr mx-3 d-none d-md-block" style={{ height: 40 }}></div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div className="text-uppercase small text-muted fw-bold" style={{ letterSpacing: 1 }}>No. of Employees</div>
                    <div className="fw-bold mb-0">{findItem("Employees").InfoField}</div>
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0 pt-0 pb-2 px-4">
                <span className="text-muted small"><i className="fas fa-info-circle me-1"></i>Pay your employees on {findItem("Payment Date").InfoField}. Record it here once you made the payment.</span>
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        {/* Deduction Summary */}
        <Row className="mb-4">
          <Col md={8}>
            <div style={{ border: '2px solid #bdbdbd', borderRadius: 10, height: '100%', display: 'flex', alignItems: 'center', padding: '1.5rem 0', background: '#fff' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div className="fw-bold mb-1">EPF</div>
                <div className="h5 mb-0">{formatCurrency(epf)}</div>
              </div>
              <div style={{ width: 2, background: '#bdbdbd', height: 50, margin: '0 1.5rem' }}></div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div className="fw-bold mb-1">ESI</div>
                <div className="h5 mb-0">{formatCurrency(esi)}</div>
              </div>
              <div style={{ width: 2, background: '#bdbdbd', height: 50, margin: '0 1.5rem' }}></div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div className="fw-bold mb-1">TDS</div>
                <div className="h5 mb-0">{formatCurrency(tdsTotal)}</div>
              </div>
            </div>
          </Col>
          <Col md={4} className="d-flex align-items-center justify-content-center">
            <div style={{ width: 180, height: 180, background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #bdbdbd' }}>
              <Doughnut data={deductionChartData} options={{ plugins: { legend: { display: false } } }} />
            </div>
          </Col>
        </Row>

        {/* Loading State */}
        {loading && (
          <Card>
            <Card.Body className="text-center py-5">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="mb-0">Loading Quick Info...</p>
            </Card.Body>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Alert variant="danger" className="d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={() => {
                const month = selectedDate.getMonth() + 1;
                const year = selectedDate.getFullYear();
                fetchQuickInfoData(month, year, selectedEmployeeType);
              }}
            >
              Retry
            </Button>
          </Alert>
        )}

        {/* Dashboard Content */}
        {!loading && !error && quickInfoData.length > 0 && (
          <>
            {/* Main Summary Cards */}
            <Row className="mb-4">
              <Col lg={8}>
                <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <Card.Body className="text-white">
                    <Row>
                      <Col md={8}>
                        <h3 className="mb-1">Employees' Net Pay</h3>
                        <h2 className="mb-2 fw-bold">{formatCurrency(findItem("Employee's Net Salary").InfoField)}</h2>
                        <p className="mb-1">Payment Date: <strong>{findItem("Payment Date").InfoField}</strong></p>
                        <p className="mb-0">Total Employees: <strong>{findItem("Employees").InfoField}</strong></p>
                      </Col>
                      <Col md={4} className="text-end">
                        <div className="display-4 opacity-25">
                          <i className="fas fa-money-bill-wave"></i>
                        </div>
                      </Col>
                    </Row>
                    {/* Net Pay Trend Chart */}
                    <div className="mt-3 bg-white rounded p-2">
                      <h6 className="text-dark mb-2 small">Net Pay Trend (Last 6 Months)</h6>
                      {trendLoading ? (
                        <div className="text-center"><Spinner animation="border" size="sm" /></div>
                      ) : (
                        <div style={{ height: '80px' }}>
                          <Line 
                            data={netPayChartData} 
                            options={{ 
                              responsive: true, 
                              maintainAspectRatio: false,
                              plugins: { legend: { display: false } },
                              scales: {
                                x: { 
                                  display: true,
                                  ticks: { font: { size: 10 } }
                                },
                                y: { 
                                  display: true,
                                  ticks: { font: { size: 10 } }
                                }
                              }
                            }} 
                          />
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4} className="d-flex flex-column h-100">
                <Card className="border-0 shadow-sm mb-3 flex-shrink-0" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', minHeight: 120 }}>
                  <Card.Body className="text-white d-flex flex-column justify-content-center text-center p-4">
                    <h5 className="mb-2">Active Employees</h5>
                    <h1 className="mb-0 fw-bold display-4">{findItem("Employees").InfoField}</h1>
                  </Card.Body>
                </Card>
                <div className="flex-grow-1 d-flex align-items-stretch">
                  <Row xs={2} className="g-2 w-100 align-content-stretch">
                    <Col className="d-flex">
                      <Card className="border-0 shadow-sm flex-fill">
                        <Card.Body className="text-center p-2 d-flex flex-column justify-content-center h-100">
                          <div className="text-muted small">Employee's Net Salary</div>
                          <div className="fw-bold">{findItem("Employee's Net Salary").InfoField}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col className="d-flex">
                      <Card className="border-0 shadow-sm flex-fill">
                        <Card.Body className="text-center p-2 d-flex flex-column justify-content-center h-100">
                          <div className="text-muted small">ESI</div>
                          <div className="fw-bold">{findItem("ESI").InfoField}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col className="d-flex">
                      <Card className="border-0 shadow-sm flex-fill">
                        <Card.Body className="text-center p-2 d-flex flex-column justify-content-center h-100">
                          <div className="text-muted small">No Of Employees</div>
                          <div className="fw-bold">{findItem("Employees").InfoField}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col className="d-flex">
                      <Card className="border-0 shadow-sm flex-fill">
                        <Card.Body className="text-center p-2 d-flex flex-column justify-content-center h-100">
                          <div className="text-muted small">RECTDS</div>
                          <div className="fw-bold">{findItem("RECTDS").InfoField}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col className="d-flex">
                      <Card className="border-0 shadow-sm flex-fill">
                        <Card.Body className="text-center p-2 d-flex flex-column justify-content-center h-100">
                          <div className="text-muted small">Payment Date</div>
                          <div className="fw-bold">{findItem("Payment Date").InfoField}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col className="d-flex">
                      <Card className="border-0 shadow-sm flex-fill">
                        <Card.Body className="text-center p-2 d-flex flex-column justify-content-center h-100">
                          <div className="text-muted small">EPF</div>
                          <div className="fw-bold">{findItem("EPF").InfoField}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </>
        )}

        {/* Detailed Sections - Only show when data is available */}
        {!loading && !error && quickInfoData.length > 0 && detailedData.length > 0 && (
          <>
            {/* Refresh Button for Table Data */}
            <Row className="mb-3">
              <Col>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-muted">Detailed Reports</h5>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={refreshTableData}
                    disabled={tableLoading}
                    className="d-flex align-items-center gap-2"
                  >
                    {tableLoading ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync-alt"></i>
                        Refresh Tables
                      </>
                    )}
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Row 1: P.F. Details Account Wise, PF Percent Wise, ESI */}
            <div style={{ position: 'relative', opacity: tableLoading ? 0.6 : 1 }}>
              {tableLoading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}>
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" className="mb-2" />
                    <div>Refreshing Tables...</div>
                  </div>
                </div>
              )}
            <Row className="mb-4 g-3">
              {/* P.F. Details Account Wise */}
              <Col xl={4} lg={6} md={12}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="text-white fw-bold" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem'
                  }}>
                    <h6 className="mb-0 fw-bold text-white">P.F. Details Account Wise</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr>
                                <th className="fw-bold border-end bg-light text-dark px-2 py-2" style={{ borderTop: '1px solid #dee2e6', width: '90px', minWidth: '70px' }}>Account</th>
                                <th className="text-end fw-bold border-end bg-light text-dark px-2 py-2" style={{ borderTop: '1px solid #dee2e6', width: '80px', minWidth: '60px' }}>Details</th>
                                <th className="text-end fw-bold bg-light text-dark px-2 py-2" style={{ borderTop: '1px solid #dee2e6', width: '100px', minWidth: '80px' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const pfAccountItems = getDetailedItems("PFAccountWise");
                            
                            const rows = pfAccountItems.map((item, index) => {
                              // Determine which column to show the value in
                              const itemName = item.ItemName || '';
                              const fieldValue = getFieldValue(item);
                              let accountValue = '';
                              let detailsValue = '';
                              let amountValue = '';
                              
                              if (itemName.includes('%')) {
                                // For percentage items, show value in Details column
                                accountValue = itemName;
                                detailsValue = fieldValue;
                                amountValue = '';
                              } else {
                                // For other account items, show value in Amount column
                                accountValue = itemName;
                                detailsValue = '';
                                amountValue = fieldValue;
                              }
                              
                                  return (
                                    <tr key={index} className={index % 2 === 0 ? '' : 'bg-light'} style={{ borderBottom: '1px solid #dee2e6' }}>
                                      <td className="small fw-medium border-end px-2 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', width: '90px', minWidth: '70px' }}>{accountValue}</td>
                                      <td className="text-end small border-end px-2 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', width: '80px', minWidth: '60px' }}>{detailsValue ? formatNumber(detailsValue) : ''}</td>
                                      <td className="text-end small fw-bold px-2 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', width: '100px', minWidth: '80px' }}>{amountValue ? formatNumber(amountValue) : ''}</td>
                                    </tr>
                                  );
                            });
                            
                            return (
                              <>
                                {rows}
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* PF Percent Wise and PF/PFund Deducted On */}
              <Col xl={4} lg={6} md={12}>
                <Row className="h-100 g-2">
                  {/* PF Percent Wise */}
                  <Col lg={8} md={6} xs={12}>
                    <Card className="h-100 shadow-sm border-0">
                      <Card.Header className="text-white fw-bold" style={{ 
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        padding: '0.75rem 1rem',
                        fontSize: '0.9rem'
                      }}>
                        <h6 className="mb-0 fw-bold text-white">PF Percent Wise</h6>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead>
                              <tr>
                                <th className="fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Percentage</th>
                                <th className="text-end fw-bold bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const pfItems = getDetailedItems("PFPERWISE");
                                
                                return (
                                  <>
                                    {pfItems.map((item, index) => (
                                      <tr key={index} className={index % 2 === 0 ? '' : 'bg-light'} style={{ borderBottom: '1px solid #dee2e6' }}>
                                        <td className="small fw-medium border-end px-3 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>{item.ItemName}</td>
                                        <td className="text-end small fw-bold px-3 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>{formatNumber(getFieldValue(item))}</td>
                                      </tr>
                                    ))}
                                  </>
                                );
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* PF/PFund Deducted On */}
                  <Col lg={4} md={6} xs={12}>
                    <Card className="h-100 shadow-sm border-0">
                      <Card.Header className="text-white fw-bold" style={{ 
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        padding: '0.75rem 1rem',
                        fontSize: '0.9rem'
                      }}>
                        <h6 className="mb-0 fw-bold text-white">Deducted on</h6>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div className="d-flex flex-column gap-2 p-3">
                          {(() => {
                            const pfonItems = getDetailedItems("PFON");
                            return (
                              <>
                                {pfonItems.map((item, index) => (
                                  <div key={index} className="d-flex flex-column align-items-center" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6', padding: '8px 0' }}>
                                    <div className="small fw-medium text-muted mb-1">{item.ItemName}</div>
                                    <div className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>{formatNumber(getFieldValue(item))}</div>
                                  </div>
                                ))}
                              </>
                            );
                          })()}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>

              {/* ESI Contribution */}
              <Col xl={4} lg={12} md={12}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="text-white fw-bold" style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem'
                  }}>
                    <h6 className="mb-0 fw-bold text-white">ESI Contribution</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr>
                            <th className="fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Rate</th>
                            <th className="text-end fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Amount</th>
                            <th className="text-center fw-bold bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Deducted on</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const esiItems = getDetailedItems("ESI");
                            const esionData = getDetailedItems("ESION");
                            const esionValue = esionData.length > 0 ? formatNumber(getFieldValue(esionData[0])) : "0";
                            
                            return (
                              <>
                                {esiItems.map((item, index) => (
                                  <tr key={index} className={index % 2 === 0 ? '' : 'bg-light'} style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td className="small fw-medium border-end px-3 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>{item.ItemName}%</td>
                                    <td className="text-end small fw-bold border-end px-3 py-2" style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>{formatNumber(getFieldValue(item))}</td>
                                    {index === 0 && (
                                      <td className="text-center small fw-bold px-3 py-2" 
                                          style={{ 
                                            backgroundColor: '#17a2b8', 
                                            color: 'white',
                                            borderBottom: esiItems.length > 1 ? `${esiItems.length}px solid #dee2e6` : '1px solid #dee2e6'
                                          }} 
                                          rowSpan={esiItems.length}>
                                        {esionValue}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 2: Employee Summary Tables */}
            <Row className="mb-4 g-3">
              {/* Total Active Employees */}
              <Col lg={4} md={6} sm={12}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="text-white fw-bold" style={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem'
                  }}>
                    <h6 className="mb-0 fw-bold text-white">Total Active Employees</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr>
                            <th className="fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}></th>
                            <th className="text-center fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Present</th>
                            <th className="text-center fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Absent</th>
                            <th className="text-center fw-bold bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const data = getEmployeeData("TotalEmpl");
                            const malePresent = parseInt(data.male) || 0;
                            const femalePresent = parseInt(data.female) || 0;
                            const maleAbsent = parseInt(data.maleAbsent) || 0;
                            const femaleAbsent = parseInt(data.femaleAbsent) || 0;
                            const totalPresent = malePresent + femalePresent;
                            const totalAbsent = maleAbsent + femaleAbsent;
                            const grandTotal = totalPresent + totalAbsent;
                            
                            return (
                              <>
                                <tr className="" style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>Male</td>
                                  <td className="text-center fw-bold text-success border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.male}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.maleAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.maleTotal}</td>
                                </tr>
                                <tr className="bg-light" style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>Female</td>
                                  <td className="text-center fw-bold text-success border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.female}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.femaleAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.femaleTotal}</td>
                                </tr>
                                <tr className="" style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>Total</td>
                                  <td className="text-center fw-bold text-primary border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{totalPresent}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{totalAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{grandTotal}</td>
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Total EPF Employees */}
              <Col lg={4} md={6} sm={12}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="text-white fw-bold" style={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem'
                  }}>
                    <h6 className="mb-0 fw-bold text-white">Employees covered in EPF</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr>
                            <th className="fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}></th>
                            <th className="text-center fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Present</th>
                            <th className="text-center fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Absent</th>
                            <th className="text-center fw-bold bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const data = getEmployeeData("TotalPFEmpl");
                            const malePresent = parseInt(data.male) || 0;
                            const femalePresent = parseInt(data.female) || 0;
                            const maleAbsent = parseInt(data.maleAbsent) || 0;
                            const femaleAbsent = parseInt(data.femaleAbsent) || 0;
                            const totalPresent = malePresent + femalePresent;
                            const totalAbsent = maleAbsent + femaleAbsent;
                            const grandTotal = totalPresent + totalAbsent;
                            
                            return (
                              <>
                                <tr className="" style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>Male</td>
                                  <td className="text-center fw-bold text-success border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.male}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.maleAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.maleTotal}</td>
                                </tr>
                                <tr className="bg-light" style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>Female</td>
                                  <td className="text-center fw-bold text-success border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.female}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.femaleAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.femaleTotal}</td>
                                </tr>
                                <tr className="" style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>Total</td>
                                  <td className="text-center fw-bold text-primary border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{totalPresent}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{totalAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{grandTotal}</td>
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Total ESIC Employees */}
              <Col lg={4} md={12} sm={12}>
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="text-white fw-bold" style={{ 
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem'
                  }}>
                    <h6 className="mb-0 fw-bold text-white">Employees covered in ESIC</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead>
                          <tr>
                            <th className="fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}></th>
                            <th className="text-center fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Present</th>
                            <th className="text-center fw-bold border-end bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Absent</th>
                            <th className="text-center fw-bold bg-light text-dark px-3 py-2" style={{ borderTop: '1px solid #dee2e6' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const data = getEmployeeData("TotalESIEmpl");
                            const malePresent = parseInt(data.male) || 0;
                            const femalePresent = parseInt(data.female) || 0;
                            const maleAbsent = parseInt(data.maleAbsent) || 0;
                            const femaleAbsent = parseInt(data.femaleAbsent) || 0;
                            const totalPresent = malePresent + femalePresent;
                            const totalAbsent = maleAbsent + femaleAbsent;
                            const grandTotal = totalPresent + totalAbsent;
                            
                            return (
                              <>
                                <tr className="" style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>Male</td>
                                  <td className="text-center fw-bold text-success border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.male}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.maleAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#fff' }}>{data.maleTotal}</td>
                                </tr>
                                <tr className="bg-light" style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>Female</td>
                                  <td className="text-center fw-bold text-success border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.female}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.femaleAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#f8f9fa' }}>{data.femaleTotal}</td>
                                </tr>
                                <tr className="" style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#e3f2fd' }}>
                                  <td className="fw-bold border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>Total</td>
                                  <td className="text-center fw-bold text-primary border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{totalPresent}</td>
                                  <td className="text-center fw-bold text-muted border-end px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{totalAbsent}</td>
                                  <td className="text-center fw-bold text-dark px-3 py-2" style={{ backgroundColor: '#e3f2fd', fontSize: '1.1rem' }}>{grandTotal}</td>
                                </tr>
                              </>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Row 3: Additional Info */}
            <Row className="mb-4">
              {/* New Joinings and Left */}
              <Col lg={4} md={6} className="mb-3">
                <Card className="text-center border-success h-100 shadow-sm">
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <h6 className="text-success mb-3">Total New Joinings</h6>
                    <h2 className="fw-bold text-success">
                      {(() => {
                        const newJoin = detailedData.find(item => item.ItemKeyName === "TotalNewJoin");
                        return newJoin ? getFieldValue(newJoin) : "0";
                      })()}
                    </h2>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={4} md={6} className="mb-3">
                <Card className="text-center border-danger h-100 shadow-sm">
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <h6 className="text-danger mb-3">Total Employees Left</h6>
                    <h2 className="fw-bold text-danger">
                      {(() => {
                        const left = detailedData.find(item => item.ItemKeyName === "TotalLeft");
                        return left ? getFieldValue(left) : "0";
                      })()}
                    </h2>
                  </Card.Body>
                </Card>
              </Col>

              {/* KYC Pending - Commented out as requested */}
              <Col lg={3}>
                <Card className="h-100">
                  <Card.Header className="bg-warning text-dark">
                    <h6 className="mb-0">KYC Pending</h6>
                  </Card.Header>
                  <Card.Body className="p-2">
                    {(() => {
                      const kycData = detailedData.filter(item => item.ItemKeyName === "KYC");
                      const getKycValue = (name) => {
                        const found = kycData.find(item => item.ItemName === name);
                        return found ? (found.InfoField1 || "0") : "0";
                      };
                      return (
                        <table className="table table-sm mb-0 text-center align-middle" style={{ background: 'transparent' }}>
                          <thead>
                            <tr>
                              <th className="fw-bold text-dark" style={{ border: 'none', background: 'transparent' }}>AADHAR</th>
                              <th className="fw-bold text-dark" style={{ border: 'none', background: 'transparent' }}>PAN</th>
                              <th className="fw-bold text-dark" style={{ border: 'none', background: 'transparent' }}>BANK</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><div className="bg-light p-1 rounded">{getKycValue("AADHAR")}</div></td>
                              <td><div className="bg-light p-1 rounded">{getKycValue("PAN")}</div></td>
                              <td><div className="bg-light p-1 rounded">{getKycValue("BANK")}</div></td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}
                  </Card.Body>
                </Card>
              </Col>

              {/* Employees Exceeding Age */}
              <Col lg={4} md={12} className="mb-3">
                <Card className="text-center border-warning h-100 shadow-sm">
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <h6 className="text-warning mb-3">Employees Exceeding 50/60 years</h6>
                    <h2 className="fw-bold text-warning">
                      {(() => {
                        const exceeds = detailedData.find(item => item.ItemKeyName === "Exceeds5060");
                        return exceeds ? getFieldValue(exceeds) : "5";
                      })()}
                    </h2>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            </div>
          </>
        )}

        {/* No Data State */}
        {!loading && !error && quickInfoData.length === 0 && (
          <Card>
            <Card.Body className="text-center py-5">
              <div className="text-muted mb-3">
                <i className="fas fa-info-circle fa-3x"></i>
              </div>
              <h5 className="text-muted">No data available</h5>
              <p className="text-muted">No quick info data found for the selected month and year.</p>
            </Card.Body>
          </Card>
        )}
      </Container>
    </>
  );
};

export default QuickInfoDashboard;
